import { type ExprNode, num, add, mul, pow, func, cloneDeep } from './node';

// ---------------------------------------------------------------------------
// Structural key helper — used to group like-base factors in mul.
// Two nodes that are structurally identical share the same key.
// ---------------------------------------------------------------------------

function structuralKey(node: ExprNode): string {
  switch (node.kind) {
    case 'num': {
      return `num(${node.value})`;
    }
    case 'var': {
      return `var(${node.name})`;
    }
    case 'add': {
      return `add(${node.children.map(structuralKey).join('|')})`;
    }
    case 'mul': {
      return `mul(${node.children.map(structuralKey).join('|')})`;
    }
    case 'pow': {
      return `pow(${structuralKey(node.base)},${structuralKey(node.exp)})`;
    }
    case 'func': {
      return `func(${node.name},${node.args.map(structuralKey).join('|')})`;
    }
  }
}

// ---------------------------------------------------------------------------
// Lightweight bottom-up simplifier
// ---------------------------------------------------------------------------

export function simplify(node: ExprNode): ExprNode {
  switch (node.kind) {
    // -------------------------------------------------------------------------
    // Atoms — return as-is
    // -------------------------------------------------------------------------

    case 'num': {
      return node;
    }

    case 'var': {
      return node;
    }

    // -------------------------------------------------------------------------
    // add
    // -------------------------------------------------------------------------

    case 'add': {
      // 1. Recursively simplify children
      const simplified = node.children.map(simplify);

      // 2. Flatten nested add nodes
      const flattened: ExprNode[] = [];
      for (const child of simplified) {
        if (child.kind === 'add') {
          flattened.push(...child.children);
        } else {
          flattened.push(child);
        }
      }

      // 3. Sum all num children; collect non-num children
      let numericSum = 0;
      const nonNumeric: ExprNode[] = [];
      for (const child of flattened) {
        if (child.kind === 'num') {
          numericSum += child.value;
        } else {
          nonNumeric.push(child);
        }
      }

      // 4. Build result children: drop zero constant if other terms exist
      const resultChildren: ExprNode[] = [...nonNumeric];
      if (numericSum !== 0 || nonNumeric.length === 0) {
        resultChildren.push(num(numericSum));
      }

      // 5. Collapse
      if (resultChildren.length === 0) return num(0);
      if (resultChildren.length === 1) return resultChildren[0]!;
      return add(resultChildren);
    }

    // -------------------------------------------------------------------------
    // mul
    // -------------------------------------------------------------------------

    case 'mul': {
      // 1. Recursively simplify children
      const simplified = node.children.map(simplify);

      // 2. Flatten nested mul nodes
      const flattened: ExprNode[] = [];
      for (const child of simplified) {
        if (child.kind === 'mul') {
          flattened.push(...child.children);
        } else {
          flattened.push(child);
        }
      }

      // 3. Short-circuit: any factor is num(0) → 0
      for (const child of flattened) {
        if (child.kind === 'num' && child.value === 0) {
          return num(0);
        }
      }

      // 4. Fold all num factors into one constant; collect non-num factors
      let numericProd = 1;
      const nonNumeric: ExprNode[] = [];
      for (const child of flattened) {
        if (child.kind === 'num') {
          numericProd *= child.value;
        } else {
          nonNumeric.push(child);
        }
      }

      // 5. Combine like-power factors.
      //    Each non-num factor is treated as pow(base, exp):
      //      - a bare factor f  → base = f, exp = 1
      //      - a pow(b, e)      → base = b, exp = e
      //    Group by structuralKey(base). For groups where all exponents are
      //    num, collapse to pow(base, num(sumOfExponents)).

      type Group = { base: ExprNode; exps: (ExprNode | null)[] };
      const groups = new Map<string, Group>();
      const groupOrder: string[] = [];

      for (const factor of nonNumeric) {
        let base: ExprNode;
        let exp: ExprNode;

        if (factor.kind === 'pow') {
          base = factor.base;
          exp = factor.exp;
        } else {
          base = factor;
          exp = num(1);
        }

        const key = structuralKey(base);
        if (!groups.has(key)) {
          groups.set(key, { base: cloneDeep(base), exps: [] });
          groupOrder.push(key);
        }
        groups.get(key)!.exps.push(exp);
      }

      // Build the new non-num factor list after combining like powers
      const combinedFactors: ExprNode[] = [];
      for (const key of groupOrder) {
        const group = groups.get(key)!;
        if (group.exps.length === 1) {
          // Single factor — reconstruct as-is
          const expNode = group.exps[0]!;
          if (expNode.kind === 'num' && expNode.value === 1) {
            combinedFactors.push(cloneDeep(group.base));
          } else {
            combinedFactors.push(pow(cloneDeep(group.base), cloneDeep(expNode)));
          }
        } else {
          // Multiple factors — only combine if ALL exps are num
          const allNum = group.exps.every((e) => e !== null && e.kind === 'num');
          if (allNum) {
            const sumExp = group.exps.reduce(
              (acc, e) => acc + (e as { kind: 'num'; value: number }).value,
              0,
            );
            combinedFactors.push(pow(cloneDeep(group.base), num(sumExp)));
          } else {
            // Leave as separate factors
            for (const e of group.exps) {
              if (e !== null) {
                if (e.kind === 'num' && e.value === 1) {
                  combinedFactors.push(cloneDeep(group.base));
                } else {
                  combinedFactors.push(pow(cloneDeep(group.base), cloneDeep(e)));
                }
              }
            }
          }
        }
      }

      // 6. Build result: drop num(1) constant if other factors exist
      const resultChildren: ExprNode[] = [];
      if (numericProd !== 1 || combinedFactors.length === 0) {
        resultChildren.push(num(numericProd));
      }
      resultChildren.push(...combinedFactors);

      // 7. Collapse
      if (resultChildren.length === 0) return num(1);
      if (resultChildren.length === 1) return resultChildren[0]!;
      return mul(resultChildren);
    }

    // -------------------------------------------------------------------------
    // pow
    // -------------------------------------------------------------------------

    case 'pow': {
      const base = simplify(node.base);
      const exp = simplify(node.exp);

      // Both numeric → evaluate
      if (base.kind === 'num' && exp.kind === 'num') {
        return num(Math.pow(base.value, exp.value));
      }

      // Exponent is 0 → 1
      if (exp.kind === 'num' && exp.value === 0) {
        return num(1);
      }

      // Exponent is 1 → base
      if (exp.kind === 'num' && exp.value === 1) {
        return base;
      }

      // base is itself a pow with numeric exponents → collapse: (b^a)^c = b^(a*c)
      if (base.kind === 'pow' && base.exp.kind === 'num' && exp.kind === 'num') {
        const innerBase = base.base;
        const combinedExp = base.exp.value * exp.value;
        return pow(innerBase, num(combinedExp));
      }

      return pow(base, exp);
    }

    // -------------------------------------------------------------------------
    // func — simplify args, keep symbolic
    // -------------------------------------------------------------------------

    case 'func': {
      const args = node.args.map(simplify);
      return func(node.name, args);
    }
  }
}
